/**
 * Block 13 PR-G Phase 2 Step 3 — pure helpers for contract text composition.
 *
 * Used by Step 4 `renderContractToPdf.ts` to format draft values into strings
 * before drawing into PDF. Section-aware substitution composition (which
 * placeholders map to which draft fields per section) lives in renderer —
 * here мы предоставляем только generic primitives.
 *
 * Helpers:
 *   - `normalizeApostrophes(text)` — заменяет typographic apostrophes
 *     (U+02BB, U+02BC, U+2018, U+2019) на ASCII `'` (U+0027). Phase 2 Step 1
 *     verification: PT Sans не содержит U+02BB; production templates uses
 *     ASCII apostrophe; helper нормализует varied user input до consistent
 *     ASCII rendering. См. ADR-0025 §Q4 amendment.
 *   - `substituteUnderscores(text, values)` — заменяет каждую `_+` sequence в
 *     `text` на соответствующий `values[i]` (sequential). Используется renderer
 *     для подстановки в template paragraphs.
 *   - `formatCurrencyDigits(amount)` — '1 234 567' (без currency symbol).
 *   - `formatCurrency(amount, code)` — '1 234 567 ₸' (с currency symbol).
 *   - `formatDate(iso, locale)` — '«23» мая 2026 года' / kk / uz equivalents.
 *   - `composeDateCityLine(template, draft)` — собирает целую dateCityLine
 *     с городом + датой. Template.dateCityLine содержит underscores + literal
 *     «» (пустые quotes), poor match для substituteUnderscores — composer
 *     генерирует строку с нуля per locale convention.
 *
 * Reference:
 *   - ADR-0025 §Q1 (template structure), §Q4 (amendment: apostrophe handling)
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 3
 */

import {
  getCurrencyShort,
  type ContractCurrencyCode,
  type ContractTemplate,
} from '@pomoshnik/shared';

import type { AmountLocale } from './amountInWords';

type CurrencyCode = ContractCurrencyCode;

/**
 * Apostrophe normalization. PT Sans does NOT include U+02BB (modifier letter
 * turned comma — официальный uz typographic apostrophe), only ASCII U+0027.
 * Production templates use ASCII (verified Phase 2 Step 1: uz.uz.json has 81
 * ASCII apostrophes, 0 U+02BB). Helper нормализует varied user input
 * (например, имя клиента `Saʻidov` с правильной типографикой) до consistent
 * ASCII rendering — иначе PDF получает `▯` (notdef glyph).
 */
export function normalizeApostrophes(text: string): string {
  return text.replace(/[ʻʼ‘’]/g, "'");
}

/**
 * Sequential underscore-run replacement. Each `_+` run в `text` заменяется
 * соответствующим `values[i]`. Если values меньше чем underscore runs —
 * оставшиеся runs остаются как `_+` (defensive: indicates renderer missed
 * a substitution; visible в PDF для debug).
 *
 * Example:
 *   substituteUnderscores('Аванс ___ тенге, остаток ___', ['100', '500'])
 *   → 'Аванс 100 тенге, остаток 500'
 */
export function substituteUnderscores(text: string, values: readonly string[]): string {
  let i = 0;
  return text.replace(/_+/g, () => {
    const v = values[i++];
    return v ?? '';
  });
}

/**
 * Digits with thin-space group separator (en-dash style). 1234567 → '1 234 567'.
 * Used inside parentheses в template paragraphs где сумма цифрами появляется
 * рядом с amountInWords result.
 *
 * Uses U+00A0 (non-breaking space) чтобы PDF render никогда не делал
 * line-break внутри числа.
 */
export function formatCurrencyDigits(amount: number): string {
  if (!Number.isFinite(amount) || !Number.isInteger(amount)) {
    throw new Error(`formatCurrencyDigits: expected integer, got ${amount}`);
  }
  const sign = amount < 0 ? '-' : '';
  const abs = Math.abs(amount);
  const str = String(abs);
  const groups: string[] = [];
  for (let i = str.length; i > 0; i -= 3) {
    groups.unshift(str.slice(Math.max(0, i - 3), i));
  }
  return sign + groups.join('\u00A0');
}

/**
 * Currency-with-symbol formatter. 1234567 + 'KZT' → '1 234 567 ₸'.
 * Currency symbol joined with regular space (PDF wrap acceptable между числом
 * и символом — visually nice).
 *
 * Phase 6.5B Amendment A1 — source of currency short symbol перешёл из
 * local map (где UZS было "so'm") в shared `getCurrencyShort`, который
 * возвращает Cyrillic "сум" для UZS. Consistent с chip-segment «тенге/рубль/сум»
 * в SectionCostAndTerms (UI ships в 6.5C).
 */
export function formatCurrency(amount: number, code: CurrencyCode): string {
  return `${formatCurrencyDigits(amount)} ${getCurrencyShort(code)}`;
}

/**
 * Month names per locale. Russian — genitive case (для "23 мая 2026 года").
 * Kazakh — nominative (followed by "жылғы" suffix). Uzbek Latin — nominative
 * (followed by "yil" suffix).
 */
const MONTHS_RU = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
] as const;

const MONTHS_KK = [
  'қаңтар',
  'ақпан',
  'наурыз',
  'сәуір',
  'мамыр',
  'маусым',
  'шілде',
  'тамыз',
  'қыркүйек',
  'қазан',
  'қараша',
  'желтоқсан',
] as const;

const MONTHS_UZ = [
  'yanvar',
  'fevral',
  'mart',
  'aprel',
  'may',
  'iyun',
  'iyul',
  'avgust',
  'sentyabr',
  'oktyabr',
  'noyabr',
  'dekabr',
] as const;

interface ParsedDate {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

function parseIsoDate(iso: string): ParsedDate {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) {
    throw new Error(`formatDate: expected YYYY-MM-DD, got ${iso}`);
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) {
    throw new Error(`formatDate: invalid month ${month} in ${iso}`);
  }
  if (day < 1 || day > 31) {
    throw new Error(`formatDate: invalid day ${day} in ${iso}`);
  }
  return { year, month, day };
}

/**
 * Locale-aware date formatter. Output formats:
 *   ru: «23» мая 2026 года
 *   kk: «23» мамыр 2026 жылғы
 *   uz: «23» may 2026 yil
 *
 * Day без leading zero ("23" не "23"; "5" не "05") — соответствует контрактной
 * convention где день не padded.
 */
export function formatDate(iso: string, locale: AmountLocale): string {
  const { year, month, day } = parseIsoDate(iso);
  // Index гарантированно 0-11 после parseIsoDate validation.
  const monthRu = MONTHS_RU[month - 1]!;
  const monthKk = MONTHS_KK[month - 1]!;
  const monthUz = MONTHS_UZ[month - 1]!;

  switch (locale) {
    case 'ru':
      return `«${day}» ${monthRu} ${year} года`;
    case 'kk':
      return `«${day}» ${monthKk} ${year} жылғы`;
    case 'uz':
      return `«${day}» ${monthUz} ${year} yil`;
    default: {
      const _exhaustive: never = locale;
      throw new Error(`formatDate: unsupported locale ${String(_exhaustive)}`);
    }
  }
}

/**
 * Compose entire `dateCityLine` per locale. Template's `dateCityLine` field
 * содержит underscores + literal «» (empty quotes) — bad fit для
 * substituteUnderscores (день должен быть INSIDE «», а template имеет их
 * empty). Composer строит строку с нуля per locale convention.
 *
 * Output: `г. {city}, «{dd}» {month} {yyyy} {yearSuffix}`
 * где city = draft.contractCity (если непустой) или template.meta.defaultCity.
 *
 * Empty/invalid date → fallback `«__»` placeholder в дате (visible debug).
 */
export function composeDateCityLine(
  template: Pick<ContractTemplate, 'meta'>,
  draft: { contractCity: string; contractDate: string },
): string {
  const city = draft.contractCity.trim() || template.meta.defaultCity;
  const locale = template.meta.lang;
  let datePart: string;
  try {
    datePart = formatDate(draft.contractDate, locale);
  } catch {
    // Defensive: до Phase 4 autosave дата может быть пустой. Renderer всё
    // равно делает draw — placeholder остаётся как visible blank.
    datePart =
      locale === 'kk'
        ? '«__» __________ 20__ жылғы'
        : locale === 'uz'
          ? '«__» __________ 20__ yil'
          : '«__» __________ 20__ года';
  }
  return `г. ${city}, ${datePart}`;
}
